import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, ArrowUpRight, Eye, Search as SearchIcon, HeartPulse,
  Coins, Users, GraduationCap, ShieldCheck, Bot, BookOpen, Sparkles,
  FlaskConical, ScanLine, Leaf, Share2, FileText, Film, MapPin, CloudSun,
  Car, Wallet, Video, Zap, PartyPopper, Soup, Newspaper, Flower2, Camera, Orbit, Download, ListChecks,
} from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import TrendingStrip from "@/components/TrendingStrip";
import Seo from "@/components/Seo";
import ThisWeekNearYou from "@/components/ThisWeekNearYou";
import HowItWorks from "@/components/HowItWorks";
import { KindText } from "@/components/KindK";

const HERO_IMG = "https://customer-assets-v7afamib.emergentagent.net/job_6dfaf7ec-672f-421f-93af-1927e0557ba9/artifacts/omso9s9o_gjwF3CaJSee2Hr3LUodYyJ-1000-80.jpg.webp";

const IMG_UNIVERSITY = "https://images.unsplash.com/photo-1758275557330-cfd545444dc3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwyfHxkaXZlcnNlJTIwcGVvcGxlJTIwY29tbXVuaXR5JTIwbW9kZXJuJTIwbGlmZXN0eWxlfGVufDB8fHx8MTc4Njg3NDc5Mnww&ixlib=rb-4.1.0&q=85";
const IMG_TRAVEL = "https://images.unsplash.com/photo-1782113314438-efd07d85d09f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjB0cmF2ZWwlMjBsaWZlc3R5bGV8ZW58MHx8fHwxNzg2ODc0NzkyfDA&ixlib=rb-4.1.0&q=85";
const IMG_VAULT = "https://images.unsplash.com/photo-1488972685288-c3fd157d7c7a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMG1vZGVybiUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3ODY4NzQ3OTJ8MA&ixlib=rb-4.1.0&q=85";

const Overline = ({ children }) => (
  <span className="text-xs uppercase tracking-[0.2em] text-brand font-medium">{children}</span>
);

// ---- Personal menu (the star of the page) ----
const PERSONAL_TILES = [
  { id: "regimen", to: "/personal?tab=regimen", label: "Health Regimen", icon: HeartPulse, desc: "My personalised wellness plan." },
  { id: "books", to: "/personal?tab=books", label: "Book Interpretation", icon: BookOpen, desc: "Summarise & interpret any book." },
  { id: "carbon", to: "/personal?tab=carbon", label: "Carbon Footprint", icon: Leaf, desc: "Measure & cut my emissions." },
  { id: "insights", to: "/personal?tab=insights", label: "Spend Analysis", icon: FileText, desc: "Own my life-pattern insights." },
  { id: "community", to: "/personal?tab=community", label: "Community", icon: Share2, desc: "Interest-based groups & posts." },
  { id: "story", to: "/personal?tab=story", label: "Life Story", icon: Film, desc: "My photos, videos & journey." },
  { id: "nearby", to: "/personal?tab=nearby", label: "Nearby", icon: MapPin, desc: "Discover & review places near you." },
  { id: "weather", to: "/personal?tab=weather", label: "Weather", icon: CloudSun, desc: "Local weather at a glance." },
];

const EARN = [
  { title: "Save & Earn", metric: "15%", desc: "Earn every time a member reuses my saved reports.", to: "/prosperity" },
  { title: "Mint Royalties", metric: "10–40%", desc: "A royalty on every use of my minted work, for life.", to: "/prosperity" },
  { title: "3-Tier Referrals", metric: "15 / 5 / 2", desc: "Earn across three generations of my network.", to: "/economy" },
  { title: "Task Bounties", metric: "+ credits", desc: "Verify AI facts and enrich data for instant rewards.", to: "/prosperity" },
];

function TopicOfDay() {
  const [topic, setTopic] = useState(null);
  useEffect(() => {
    let cancel = false;
    api.get("/galactopedia/topic-of-day")
      .then(({ data }) => { if (!cancel) setTopic(data.topic || null); })
      .catch(() => {});
    return () => { cancel = true; };
  }, []);
  if (!topic) return null;
  return (
    <Link
      to={`/galactopedia?t=${topic.slug}`}
      data-testid="topic-of-day-card"
      className="group block rud-card rounded-2xl p-7 sm:p-9 border border-rud hover:border-brand/50 hover:-translate-y-1 transition-all relative overflow-hidden"
    >
      <div className="absolute -top-16 -right-10 w-64 h-64 brand-glow rounded-full pointer-events-none opacity-60" />
      <div className="relative flex flex-col sm:flex-row sm:items-center gap-6 justify-between">
        <div className="max-w-2xl">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-sec mb-3">
            <Sparkles size={13} className="text-brand" /> Topic of the Day
            {topic.views > 0 && <span className="inline-flex items-center gap-1"><Eye size={12} /> {topic.views}</span>}
          </div>
          <h3 className="font-serif text-3xl sm:text-4xl tracking-tight leading-[1.05] group-hover:text-brand transition-colors">{topic.title}</h3>
          <p className="mt-3 text-sm text-sec leading-relaxed">{topic.snippet}</p>
        </div>
        <span className="shrink-0 inline-flex items-center gap-2 text-sm text-brand font-medium">
          Explore <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
        </span>
      </div>
    </Link>
  );
}

// ---- Global Community image card ----
function CommunityImageCard({ to, img, overline, title, desc, testid, index }) {
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.06 }}>
      <Link to={to} data-testid={testid}
        className="group relative overflow-hidden rounded-2xl border border-rud hover:border-brand/60 transition-all min-h-[300px] flex flex-col justify-end p-8">
        <img src={img} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:scale-[1.05] transition-transform duration-500" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/10" />
        <div className="relative">
          <span className="text-xs uppercase tracking-[0.2em] text-white/80 font-medium">{overline}</span>
          <h3 className="font-serif text-2xl sm:text-3xl tracking-tight text-white mt-2 leading-tight">{title}</h3>
          <p className="text-white/70 text-sm mt-2 leading-relaxed max-w-sm">{desc}</p>
          <span className="inline-flex items-center gap-1.5 text-white text-sm mt-4 font-medium group-hover:gap-2.5 transition-all">Explore <ArrowUpRight size={15} /></span>
        </div>
      </Link>
    </motion.div>
  );
}

function CommunitySolidCard({ to, icon: Icon, overline, title, desc, testid, index, highlight }) {
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.06 }}>
      <Link to={to} data-testid={testid}
        className={`group flex flex-col justify-between h-full min-h-[220px] rud-card rounded-2xl p-8 hover:-translate-y-1 transition-all relative overflow-hidden ${highlight ? "border-brand/60 bg-elevated hover:border-brand" : "hover:border-brand/50"}`}>
        {highlight && <div className="absolute -top-14 -right-8 w-52 h-52 brand-glow rounded-full pointer-events-none opacity-40" />}
        <div className="relative">
          <Icon size={26} className="text-brand" />
          <Overline><span className="block mt-4">{overline}</span></Overline>
          <h3 className="font-serif text-2xl tracking-tight mt-2 leading-tight group-hover:text-brand transition-colors">{title}</h3>
          <p className="text-sec text-sm mt-2 leading-relaxed">{desc}</p>
        </div>
        <span className="inline-flex items-center gap-1.5 text-brand text-sm mt-6 group-hover:gap-2.5 transition-all">Open <ArrowUpRight size={15} /></span>
      </Link>
    </motion.div>
  );
}

export default function Landing() {
  const { user } = useApp();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const installApp = async () => {
    const evt = window.__mykindInstallPrompt;
    if (evt) { try { evt.prompt(); await evt.userChoice; window.__mykindInstallPrompt = null; return; } catch (_) { /* fall through */ } }
    toast("Install My Kind on your device", { description: "Android/Chrome: open the browser menu and tap “Install app”. iPhone: tap Share, then “Add to Home Screen”." });
  };


  const go = (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    const dest = `/search?q=${encodeURIComponent(q.trim())}`;
    if (user === null) {
      try { sessionStorage.setItem("rud_post_login_redirect", dest); } catch (_) {}
      navigate("/auth");
      return;
    }
    navigate(dest);
  };

  return (
    <div data-testid="landing-page">
      <Seo
        title="Kindness, Connection, Yoga, Wellbeing & Longevity — as one family"
        description="My Kind is a kindness-first conscious community to connect with your kind of people, practice yoga & mindfulness, boost wellbeing & longevity, learn, earn and give back through philanthropy."
        keywords="kindness, kindness social media, conscious community, connect with people, yoga, meditation, mindfulness, wellbeing, wellness, longevity, healthy living, philanthropy, community, product safety scanner, non-toxic living"
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            { "@type": "Question", "name": "What is My Kind?", "acceptedAnswer": { "@type": "Answer", "text": "My Kind is a kindness-first conscious community platform to connect with like-minded people, practice yoga and mindfulness, improve wellbeing and longevity, learn, earn and give back through philanthropy." } },
            { "@type": "Question", "name": "Is My Kind a social media for kindness?", "acceptedAnswer": { "@type": "Answer", "text": "Yes — My Kind is a kinder, privacy-first alternative to conventional social media, built around genuine connection, wellbeing and community giving." } },
            { "@type": "Question", "name": "What is the Product Safety scanner?", "acceptedAnswer": { "@type": "Answer", "text": "Scan any grocery, shopping or restaurant receipt or ingredient label and My Kind flags harmful ingredients and toxic chemicals, tallies calories and macros, grades the purchase and suggests cleaner, non-toxic swaps." } }
          ]
        }}
      />
      {/* ---------- HERO ---------- */}
      <section className="relative overflow-hidden border-b border-rud">
        <img src={HERO_IMG} alt="" className="hero-bg-img absolute inset-0 w-full h-full object-cover opacity-[0.08] pointer-events-none" />
        <div className="absolute -top-24 left-1/3 w-[28rem] h-[28rem] brand-glow rounded-full pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-5 sm:px-8 pt-24 pb-14 text-center">
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02]">
              My Universe, Connected.
            </h1>
            <div data-testid="hero-tags" className="flex flex-wrap items-center justify-center gap-2 sm:gap-2.5 mt-5">
              {[
                { label: "Connect", Icon: Users },
                { label: "Have Fun", Icon: PartyPopper },
                { label: "Learn", Icon: GraduationCap },
                { label: "Earn", Icon: Coins },
                { label: "Be Well", Icon: HeartPulse },
              ].map(({ label, Icon }) => (
                <span key={label} data-testid={`hero-tag-${label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="inline-flex items-center gap-1.5 text-[11px] sm:text-xs font-semibold uppercase tracking-[0.18em] text-brand bg-brand/10 border border-brand/25 rounded-full px-3.5 py-1.5 transition-colors hover:bg-brand/15">
                  <Icon size={14} /> {label}
                </span>
              ))}
            </div>
            <p data-testid="hero-personal-line" className="text-sec text-sm sm:text-base max-w-2xl mx-auto mt-5 leading-relaxed">
              My <KindText text="Kind" /> social profile, my communities and network, health regimen, books, documents &amp; receipts, interpretation, habits, my life story, Community Travel, Events &amp; Food — and a personal assistant for everything personalised. All owned by you.
            </p>

            {/* Command bar — moved up */}
            <form onSubmit={go} data-testid="home-search" className="mt-7 max-w-3xl mx-auto">
              <div className="flex items-center gap-2 border border-rud bg-elevated p-2 rounded-2xl focus-within:border-brand transition-colors">
                <SearchIcon size={18} className="text-sec ml-3 shrink-0" />
                <input
                  data-testid="home-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Clean & Ad-free"
                  className="bg-transparent border-none text-base sm:text-lg w-full px-2 py-3 focus:outline-none placeholder:text-sec"
                />
                <button type="submit" data-testid="home-search-btn"
                  className="btn-primary px-5 sm:px-7 py-3 rounded-xl flex items-center gap-2 whitespace-nowrap shrink-0">
                  <span className="hidden sm:inline">Ask or Search and Earn</span><span className="sm:hidden">Ask</span>
                </button>
              </div>
            </form>

            {/* Kind Social primary CTA — right under the search bar */}
            <div className="mt-4 flex justify-center">
              <Link to="/personal" data-testid="hero-connect-post-share"
                className="inline-flex items-center gap-2 text-sm btn-primary rounded-full px-6 py-3">
                <HeartPulse size={16} /> My<KindText text="Kind" /> — Connect, Post &amp; Share
              </Link>
            </div>
            {/* One-line quick access */}
            <div className="mt-4 flex flex-wrap items-center justify-center gap-2" data-testid="hero-quick-links">
              <Link to="/personal?tab=regimen" data-testid="quick-regimen" className="inline-flex items-center gap-1.5 text-sm rud-card rounded-full px-4 py-2 hover:border-brand/60 transition-colors"><HeartPulse size={14} className="text-brand" /> Health Regimen</Link>
              <Link to="/personal?tab=habits" data-testid="quick-habits" className="inline-flex items-center gap-1.5 text-sm rud-card rounded-full px-4 py-2 hover:border-brand/60 transition-colors"><ListChecks size={14} className="text-brand" /> Habits</Link>
              <Link to="/personal?tab=books" data-testid="quick-books" className="inline-flex items-center gap-1.5 text-sm rud-card rounded-full px-4 py-2 hover:border-brand/60 transition-colors"><BookOpen size={14} className="text-brand" /> Book Interpretation</Link>
              <Link to="/personal?tab=receipts" data-testid="quick-purity" className="inline-flex items-center gap-1.5 text-sm rud-card rounded-full px-4 py-2 hover:border-brand/60 transition-colors"><FlaskConical size={14} className="text-brand" /> Product Safety</Link>
              <Link to="/learn" data-testid="quick-learn" className="inline-flex items-center gap-1.5 text-sm rud-card rounded-full px-4 py-2 hover:border-brand/60 transition-colors"><GraduationCap size={14} className="text-brand" /> VishwaGuru (Learn &amp; Earn)</Link>
              <Link to="/transport" data-testid="quick-travel" className="inline-flex items-center gap-1.5 text-sm rud-card rounded-full px-4 py-2 hover:border-brand/60 transition-colors"><Car size={14} className="text-brand" /> Community Travel</Link>
              <Link to="/events" data-testid="quick-events" className="inline-flex items-center gap-1.5 text-sm rud-card rounded-full px-4 py-2 hover:border-brand/60 transition-colors"><PartyPopper size={14} className="text-brand" /> Community Events</Link>
              <Link to="/meals" data-testid="quick-meals" className="inline-flex items-center gap-1.5 text-sm rud-card rounded-full px-4 py-2 hover:border-brand/60 transition-colors"><Soup size={14} className="text-brand" /> Community Food</Link>
              <Link to="/news" data-testid="quick-news" className="inline-flex items-center gap-1.5 text-sm rud-card rounded-full px-4 py-2 hover:border-brand/60 transition-colors"><Newspaper size={14} className="text-brand" /> Good News</Link>
              <Link to="/practice" data-testid="quick-practice" className="inline-flex items-center gap-1.5 text-sm rud-card rounded-full px-4 py-2 hover:border-brand/60 transition-colors"><Flower2 size={14} className="text-brand" /> Daily Practice</Link>
              <Link to="/galactopedia" data-testid="quick-galactopedia" title="The collective brain of the community" className="inline-flex items-center gap-1.5 text-sm rud-card rounded-full px-4 py-2 hover:border-brand/60 transition-colors"><Orbit size={14} className="text-brand" /> Galactopedia — Collective Brain</Link>
            </div>

            {/* Film, then app download — lower, left-aligned */}
            <div className="mt-7 flex flex-col items-center sm:items-start gap-3" data-testid="hero-film-download">
              <Link to="/watch" data-testid="hero-watch-film"
                className="inline-flex items-center gap-2 text-sm btn-primary rounded-full px-6 py-3">
                <span className="grid place-items-center w-6 h-6 rounded-full bg-black/20 text-white text-[10px]">▶</span>
                Watch how My Kind works (2-min film)
              </Link>
              <button type="button" data-testid="hero-app-download" onClick={installApp}
                className="inline-flex items-center gap-2 text-sm rud-card rounded-full px-6 py-3 hover:border-brand/60 transition-colors">
                <Download size={15} className="text-brand" /> Download the My<KindText text="Kind" /> app
              </button>
            </div>

            <div className="mt-7 space-y-2">
              <div>
                <span data-testid="hero-vasudhaiva" className="inline-block italic font-serif font-medium text-base sm:text-xl leading-snug px-4 py-1.5 rounded-lg"
                  style={{ backgroundColor: "var(--rud-text)", color: "var(--rud-bg)" }}>
                  Vasudhaiva Kutumbakam — the world is one family.
                </span>
              </div>
              <div>
                <span data-testid="hero-mission" className="inline-block font-serif font-medium text-base sm:text-xl leading-snug px-4 py-1.5 rounded-lg"
                  style={{ backgroundColor: "var(--rud-text)", color: "var(--rud-bg)" }}>
                  The Power of AI to everyone to do everything.
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ---------- HOW MY KIND WORKS (animated explainer) ---------- */}
      <HowItWorks />

      {/* ---------- PERSONAL ECOSYSTEM (bento) ---------- */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 sm:pt-20" data-testid="personal-menu-section">
        <div className="mb-8">
          <Overline>My Personal space</Overline>
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight mt-3 leading-none">Everything personal, in one place.</h2>
          <p className="text-sec text-sm mt-3 max-w-xl">Jump straight into any part of my My Kind personal menu — health, purity, money, stories and more.</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {/* Feature: My Profile */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.02 }} className="col-span-2 lg:col-span-2">
            <Link to="/personal" data-testid="personal-menu-profile"
              className="group flex h-full flex-col justify-between rud-card rounded-2xl p-7 hover:-translate-y-1 hover:border-brand/50 transition-all relative overflow-hidden min-h-[220px]">
              <div className="absolute -top-14 -right-8 w-52 h-52 brand-glow rounded-full pointer-events-none opacity-50" />
              <div className="relative">
                <HeartPulse size={30} className="text-brand" />
                <h3 className="font-serif text-3xl tracking-tight mt-4 group-hover:text-brand transition-colors"><KindText text="The Kind Social" /></h3>
                <p className="text-sec text-sm mt-2 leading-relaxed max-w-md">My personal assistant — health regimen, document interpretation, habits and my life story, all owned by you.</p>
              </div>
              <span className="relative inline-flex items-center gap-1.5 text-brand text-sm mt-6 group-hover:gap-2.5 transition-all">Open my space <ArrowUpRight size={15} /></span>
            </Link>
          </motion.div>

          {/* Feature: Product Safety (highlighted) */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.05 }} className="col-span-2 lg:col-span-2">
            <Link to="/personal?tab=receipts" data-testid="personal-menu-purity"
              className="group flex h-full flex-col justify-between rud-card bg-elevated border-brand/60 rounded-2xl p-7 hover:-translate-y-1 hover:border-brand transition-all relative overflow-hidden min-h-[220px]">
              <div className="absolute -top-14 -right-8 w-52 h-52 brand-glow rounded-full pointer-events-none opacity-50" />
              <div className="relative">
                <div className="flex flex-wrap items-center gap-2">
                  <FlaskConical size={26} className="text-brand" />
                  <span className="text-[10px] uppercase tracking-widest text-black bg-brand rounded-full px-2 py-0.5 font-semibold" data-testid="purity-teaser-free">First scan free ✨</span>
                </div>
                <h3 className="font-serif text-3xl tracking-tight mt-4 group-hover:text-brand transition-colors">Product Safety · Molecular Scanner</h3>
                <p className="text-sec text-sm mt-2 leading-relaxed max-w-md">Scan any label or receipt. My Kind flags harmful ingredients, tallies calories &amp; macros, grades my purchase and suggests cleaner swaps.</p>
              </div>
              <span className="relative inline-flex items-center gap-2 btn-primary rounded-full h-10 px-5 text-sm w-max mt-6"><ScanLine size={16} /> Try a free scan</span>
            </Link>
          </motion.div>

          {/* Small tiles */}
          {PERSONAL_TILES.map((tile, i) => (
            <motion.div key={tile.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.05 + i * 0.04 }} className="col-span-1">
              <Link to={tile.to} data-testid={`personal-menu-${tile.id}`}
                className="group flex h-full flex-col justify-between rud-card rounded-2xl p-5 hover:-translate-y-1 hover:border-brand/50 transition-all min-h-[150px]">
                <tile.icon size={20} className="text-brand" />
                <div className="mt-3">
                  <div className="font-serif text-lg tracking-tight group-hover:text-brand transition-colors">{tile.label}</div>
                  <p className="text-sec text-xs mt-1 leading-relaxed">{tile.desc}</p>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ---------- GLOBAL COMMUNITY ---------- */}
      <section className="mt-24 sm:mt-32 py-20 sm:py-24 border-y border-rud relative" data-testid="global-community-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="mb-10">
            <Overline>The Global Community</Overline>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight mt-3 leading-none">Learn, travel &amp; own — together.</h2>
            <p className="text-sec text-sm mt-3 max-w-xl">A decentralised, democratic ecosystem where every member learns, earns and belongs.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <CommunitySolidCard index={0} testid="community-university" to="/learn" icon={GraduationCap}
              overline="VishwaGuru-University · Teacher of The World" title="VishwaGuru-University" desc="10,000+ courses on every subject — from Yoga to AI & Quantum Computing — with exams, certificates and a learn-to-earn economy for the whole community." />
            <CommunitySolidCard index={1} testid="community-travel" to="/transport" icon={Car}
              overline="Global Community Travel" title="Community Travel" desc="Share rides, plan group tours and stay with fellow members — kinder and cheaper than the alternatives." />
            <CommunitySolidCard index={2} testid="community-vault" to="/vault" icon={ShieldCheck}
              overline="The Vault" title="My Data Vault" desc="Own my data & IP, earn from anonymised insights, and host my apps — sovereignty by design." />

            <CommunitySolidCard index={4} testid="community-meet" to="/meet" icon={Video}
              overline="Secure Meet" title="End-to-end Video" desc="Launch a secure My Kind video room and invite anyone with a link." />
            <CommunitySolidCard index={5} testid="community-prosperity" to="/prosperity" icon={Zap} highlight
              overline="Prosperity-Mint Money" title="My Earning Hub" desc="Credits, 3-tier referrals and lifetime royalties from my minted work." />
            <CommunitySolidCard index={6} testid="community-events" to="/events" icon={PartyPopper}
              overline="Community Events & Meals" title="Gather Together" desc="Host or join birthdays, weddings, house-warmings, ceremonies & home-cooked meals near you — one warm family." />
          </div>
        </div>
      </section>

      {/* ---------- MARKET STRIP + TOPIC OF DAY ---------- */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 space-y-14">
        <TrendingStrip scope="" title="Trending on the Mint Market" limit={8} />
        <TopicOfDay />
        <ThisWeekNearYou />
      </section>

      {/* ---------- LUCRATIVE EARN MECHANICS ---------- */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <Overline>The Autonomous Economy</Overline>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight mt-3 leading-none">My data. My wealth.</h2>
          </div>
          <p className="text-sec text-sm max-w-sm">Four ways every member earns — front and center, no fine print.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {EARN.map((c, i) => (
            <motion.div
              key={c.title}
              initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.07 }}
              className="rud-card rounded-2xl p-8 flex flex-col hover:-translate-y-1 hover:border-brand/50 transition-all" data-testid={`earn-card-${i}`}
            >
              <div className="text-4xl sm:text-5xl font-serif text-brand tracking-tighter leading-none">{c.metric}</div>
              <h3 className="text-xl font-medium mt-5">{c.title}</h3>
              <p className="text-sec text-sm mt-2 leading-relaxed">{c.desc}</p>
              <Link to={c.to} className="inline-flex items-center gap-1.5 text-sm text-[var(--rud-text)] hover:text-brand transition-colors mt-auto pt-6">
                Start earning <ArrowRight size={15} />
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ---------- FINAL CTA ---------- */}
      <section className="border-t border-rud">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 text-center relative overflow-hidden">
          <div className="absolute -bottom-24 left-1/2 -translate-x-1/2 w-[30rem] h-[30rem] brand-glow rounded-full pointer-events-none" />
          <ShieldCheck className="mx-auto text-brand mb-5" size={30} />
          <h2 className="font-serif text-4xl sm:text-6xl tracking-tighter leading-none max-w-3xl mx-auto">Begin my journey.</h2>
          <p className="text-sec mt-5 max-w-xl mx-auto">Ask a question, scan a label, learn a skill or claim my data. My Kind handles the rest — in my language.</p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-9">
            <Link to="/search" data-testid="final-cta-search" className="btn-primary h-12 px-7 inline-flex items-center gap-2">
              <SearchIcon size={17} /> Ask or Search and Earn
            </Link>
            <Link to="/personal" data-testid="final-cta-personal" className="btn-ghost rounded-full h-12 px-7 inline-flex items-center gap-2">
              <HeartPulse size={17} /> Explore my Personal space <ArrowRight size={17} />
            </Link>
          </div>
        </div>
      </section>

      {/* ---------- WHY / PRINCIPLES (page footer band) ---------- */}
      <section className="border-t border-rud" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
          <Overline>Why My Kind</Overline>
          <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mt-3 leading-tight max-w-2xl">Built on four promises to every member.</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-8">
            {[
              { t: "Decentralized", d: "No single gatekeeper — power lives with the community, not a corporation." },
              { t: "Democratic", d: "Everyone gets a voice, a vote and a fair share of what we build together." },
              { t: "Distributed", d: "Value and opportunity flow across the whole network, everywhere on Earth." },
              { t: "Data-Sovereign", d: "You own my data and IP — and you earn whenever it creates value." },
            ].map((p) => (
              <div key={p.t} className="border-t-2 border-brand/40 pt-4">
                <div className="font-serif text-xl tracking-tight">{p.t}</div>
                <p className="text-sec text-sm mt-1.5 leading-relaxed">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
